import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieOptions, CookieService } from 'ngx-cookie';
import { ActivatedRoute } from '@angular/router';

import { ConfigurationService } from '@reg/shared/service';
import { AuthUser } from '@reg/shared/model';

/**
 * This service handles verification of a user's authentication token with
 * FloSports.  It should not be relied upon for true security since it is
 * client-side, but should be fine to use to control UX elements.
 */
@Injectable()
export class AuthService {
    private token: string;
    private _user: AuthUser;

    constructor(
                private configService: ConfigurationService,
                private cookieService: CookieService,
                private activatedRoute: ActivatedRoute,
    ) {
        this.token = this.loadToken();
        this._user = this.parseUser();
    }

    public getAccessDeniedMsg(): string {
        const email: string = this.user && this.user.email;

        if (email) {
            return `Access denied for ${email}`;
        }

        return 'Access denied';
    }

    public isLoggedIn(): boolean {
        return !!(this.user && this.user.id);
    }

    /**
     * Check if user is admin
     *
     * @returns {boolean}
     */
    public isUserAdmin(): boolean {
        return this.user && this.user.isAdmin();
    }

    public get user(): AuthUser {
        return this._user;
    }

    public getNextParam(): string {
        let nextParam: string = this.activatedRoute.snapshot.queryParams['next'];

        if (!nextParam) {
            nextParam = document.location.href;
        }

        return nextParam;
    }

    public logout(returnToCurrentPage: boolean = false): void {
        this.removeJwtCookies();

        if (returnToCurrentPage) {
            location.reload();
        } else {
            const header: any = this.configService.getSportConfig('header');
            window.location.href = header.siteUrl;
        }
    }

    public removeJwtCookies(): void {
        const jwtToken: string = this.cookieService.get('jwt_token');
        const cookieOptions: CookieOptions = this.getJwtCookieOptions(jwtToken);
        this.cookieService.remove('jwt_refresh_token', cookieOptions);
        this.cookieService.remove('jwt_token', cookieOptions);
    }

    public setJwtCookies(jwtToken: string, jwtRefreshToken: string): void {
        const cookieOptions: CookieOptions = this.getJwtCookieOptions(jwtToken);
        this.cookieService.put('jwt_token', jwtToken, cookieOptions);
        this.cookieService.put('jwt_refresh_token', jwtRefreshToken, cookieOptions);
    }

    private getJwtCookieOptions(jwtToken: string): CookieOptions {
        if (!jwtToken) {
            return null;
        }

        const jwtHelper: JwtHelperService = new JwtHelperService();
        const parsedToken: any = jwtHelper.decodeToken(jwtToken);
        const expiration: Date = new Date(parsedToken.exp * 1000);
        const upperLevelDomain: string = this.getUpperLevelDomainName();

        // If running locally the browser doesn't like setting the domain to .localhost
        const cookieOptions: CookieOptions = {
            path: '/',
            domain: upperLevelDomain === 'localhost' ? upperLevelDomain : `.${upperLevelDomain}`,
            expires: expiration,
            secure: false,
        };

        return cookieOptions;
    }

    private getUpperLevelDomainName(): string {
        const hostname: string = window.location.hostname;
        let upperLevelDomain: string = hostname;
        const parts: string[] = hostname.split('.');

        if (parts.length > 2) {
            // Remove the sub domain
            parts.shift();
            // Join the domain back together
            upperLevelDomain = parts.join('.');
        }

        return upperLevelDomain;
    }

    private loadToken(): string {
        return this.cookieService.get('jwt_token');
    }

    private parseUser(): AuthUser {
        const jwtHelper: JwtHelperService = new JwtHelperService();

        if (!this.token || jwtHelper.isTokenExpired(this.token)) {
            this.removeJwtCookies();
            return null;
        }

        const user: any = jwtHelper.decodeToken(this.token);

        if (user && user.id) {
            const currentSiteId: number = this.configService.getSportConfig('siteId');
            return new AuthUser(
                user.id,
                user.email,
                user.roles,
                user.sites,
                user.isPremiumSubscriber,
                currentSiteId,
                user.profile_picture,
            );
        }

        return null;
    }
}
